import { useMemo } from "react";
import {
  useBenefitPlanFields,
  type AiFieldLookup,
} from "@platform/document-library";
import { useProposalDocumentsQuery } from "@platform/propelx-documents";

/**
 * Fetches AI Dox extraction data for the RFP's extracted carrier proposal
 * documents, as the ai-dox proposals list returns them: each carrier's latest
 * upload. Comps
 * spans all coverages, so no coverage filter applies — the lookup is keyed by
 * proposal plan id, which only matches the cells of its own coverage. While a
 * carrier's latest upload is a pending requote, its plans aren't the active
 * quote Comps shows, so that carrier's cells find no extraction.
 */
export const useCompsAiFieldLookup = (ecoRfpId: string): AiFieldLookup => {
  const { proposals } = useProposalDocumentsQuery(ecoRfpId);

  const { documentIds, documentDownloadUrls } = useMemo(() => {
    // A fields response fetched mid-extraction is empty and nothing here
    // refetches it, so a document joins the query only once it is extracted.
    const documents = proposals
      .flatMap((proposal) => proposal.documents)
      .filter((document) => document.status === "Extracted");
    const urls = Object.fromEntries(
      documents.flatMap((document) =>
        document.downloadUrl ? [[document.id, document.downloadUrl]] : [],
      ),
    );
    return {
      documentIds: [...new Set(documents.map((document) => document.id))],
      documentDownloadUrls: Object.keys(urls).length > 0 ? urls : undefined,
    };
  }, [proposals]);

  const { fieldLookup } = useBenefitPlanFields(documentIds, {
    enabled: documentIds.length > 0,
    documentDownloadUrls,
  });

  return fieldLookup;
};
